import { Container, Text } from "pixi.js";
import { BoardSquare } from "./BoardSquare";
import { LetterTile } from "./LetterTile";
import { ParticleSystem } from "./ParticleSystem";
import type { PlacedTile, PremiumSquare, WordGrid, GridHint } from "../../../shared/types";
import { BOARD_SIZE, DEBUG } from "../../../shared/constants";

const CELL_SIZE = 40;

export class BoardScene extends Container {
  private bgLayer = new Container();
  private tileLayer = new Container();
  private overlayLayer = new Container();
  private particleLayer = new ParticleSystem();
  private floatLayer = new Container();
  private debugLayer = new Container();
  private squares: BoardSquare[][] = [];
  private tileSprites: Map<string, LetterTile> = new Map();
  private pendingAnimations = new Set<string>();
  private boardSize = BOARD_SIZE;
  private currentScale = 1;
  private opponentSolvedKeys = new Set<string>();
  private clueHighlightKeys = new Set<string>();
  private debugSolution: Record<string, string> | null = null;

  onCellClick?: (x: number, y: number) => void;
  onTileClick?: (tileId: string, letter: string, x: number, y: number) => void;

  constructor() {
    super();
    this.addChild(this.bgLayer, this.tileLayer, this.overlayLayer, this.particleLayer, this.floatLayer, this.debugLayer);
    this.debugLayer.visible = DEBUG;
    this.buildGrid();
  }

  private buildGrid() {
    for (let y = 0; y < this.boardSize; y++) {
      this.squares[y] = [];
      for (let x = 0; x < this.boardSize; x++) {
        const sq = new BoardSquare(x, y, CELL_SIZE);
        sq.x = x * CELL_SIZE;
        sq.y = y * CELL_SIZE;
        sq.onCellClick = (cx, cy) => {
          if (this.onCellClick) this.onCellClick(cx, cy);
        };
        this.bgLayer.addChild(sq);
        this.squares[y][x] = sq;
      }
    }
  }

  setPuzzle(puzzle: WordGrid | null) {
    if (!puzzle) return;
    this.clueHighlightKeys.clear();
    this.boardSize = puzzle.width;

    // Rebuild grid with correct size
    this.bgLayer.removeChildren();
    this.squares = [];
    for (let y = 0; y < puzzle.height; y++) {
      this.squares[y] = [];
      for (let x = 0; x < puzzle.width; x++) {
        const sq = new BoardSquare(x, y, CELL_SIZE);
        sq.x = x * CELL_SIZE;
        sq.y = y * CELL_SIZE;
        sq.onCellClick = (cx, cy) => {
          if (this.onCellClick) this.onCellClick(cx, cy);
        };
        this.bgLayer.addChild(sq);
        this.squares[y][x] = sq;
      }
    }

    // Set black squares
    for (const key of puzzle.blackSquares) {
      const [x, y] = key.split(",").map(Number);
      if (this.squares[y]?.[x]) {
        this.squares[y][x].setBlack();
      }
    }
    // Set clue numbers
    for (const clue of puzzle.clues) {
      const sq = this.squares[clue.startY]?.[clue.startX];
      if (sq) {
        sq.clueNumber = clue.number;
      }
    }

    // Apply opponent solved hints if any
    this.updateOpponentSolvedHints();
  }

  setScale(canvasWidth: number, canvasHeight: number) {
    if (canvasWidth <= 0 || canvasHeight <= 0) return;
    const gridWidth = this.boardSize * CELL_SIZE;
    const gridHeight = (this.squares.length || this.boardSize) * CELL_SIZE;
    const scale = Math.min(canvasWidth / gridWidth, canvasHeight / gridHeight);
    this.currentScale = scale;
    this.scale.set(scale);
    // Center the grid
    this.x = (canvasWidth - gridWidth * scale) / 2;
    this.y = (canvasHeight - gridHeight * scale) / 2;
  }

  screenToGrid(screenX: number, screenY: number): { x: number; y: number } | null {
    if (this.currentScale <= 0) return null;
    const localX = (screenX - this.x) / this.currentScale;
    const localY = (screenY - this.y) / this.currentScale;
    const x = Math.floor(localX / CELL_SIZE);
    const y = Math.floor(localY / CELL_SIZE);
    if (x < 0 || x >= this.boardSize || y < 0 || y >= this.squares.length) {
      return null;
    }
    return { x, y };
  }

  applyPremiumSquares(premiums: PremiumSquare[]) {
    for (const p of premiums) {
      const sq = this.squares[p.y]?.[p.x];
      if (sq) {
        sq.setPremium(p.type);
      }
    }
  }

  setOpponentSolvedKeys(keys: Set<string>) {
    this.opponentSolvedKeys = keys;
    this.updateOpponentSolvedHints();
  }

  private updateOpponentSolvedHints() {
    for (let y = 0; y < this.squares.length; y++) {
      for (let x = 0; x < this.squares[y]?.length; x++) {
        const sq = this.squares[y][x];
        if (sq) {
          sq.setOpponentSolved(this.opponentSolvedKeys.has(`${x},${y}`));
        }
      }
    }
  }

  updateBoard(placedTiles: PlacedTile[], animateNew = false) {
    const existingKeys = new Set(this.tileSprites.keys());
    const newKeys = new Set(placedTiles.map((pt) => `${pt.x},${pt.y}`));

    // Animate removed tiles
    for (const key of existingKeys) {
      if (!newKeys.has(key)) {
        const sprite = this.tileSprites.get(key);
        if (sprite) {
          const [x, y] = key.split(",").map(Number);
          this.particleLayer.spawnDust(
            x * CELL_SIZE + CELL_SIZE / 2,
            y * CELL_SIZE + CELL_SIZE / 2
          );
          sprite.popOut().then(() => {
            // cleanup handled by removeChildren below
          });
        }
      }
    }

    // Rebuild tile layer
    this.tileLayer.removeChildren();
    this.tileSprites.clear();

    for (const pt of placedTiles) {
      const sprite = new LetterTile(
        pt.letter,
        1,
        CELL_SIZE,
        this.squares[pt.y]?.[pt.x]?.premiumType ?? null
      );
      sprite.x = pt.x * CELL_SIZE;
      sprite.y = pt.y * CELL_SIZE;

      sprite.eventMode = "static";
      sprite.cursor = "pointer";
      sprite.on("pointerdown", () => {
        if (this.onTileClick) {
          this.onTileClick(pt.tileId, pt.letter, pt.x, pt.y);
        }
      });

      this.tileLayer.addChild(sprite);
      this.tileSprites.set(`${pt.x},${pt.y}`, sprite);

      if (animateNew && !existingKeys.has(`${pt.x},${pt.y}`)) {
        this.pendingAnimations.add(`${pt.x},${pt.y}`);
        sprite.popIn().then(() => {
          this.particleLayer.spawnDust(
            pt.x * CELL_SIZE + CELL_SIZE / 2,
            pt.y * CELL_SIZE + CELL_SIZE / 2
          );
          this.pendingAnimations.delete(`${pt.x},${pt.y}`);
        });
      }
    }
  }

  async revealTiles(solution: Record<string, string>, staggerMs = 80) {
    const entries = Object.entries(solution);
    for (let i = 0; i < entries.length; i++) {
      const [key, letter] = entries[i];
      const [x, y] = key.split(",").map(Number);
      const sprite = this.tileSprites.get(key);

      if (sprite) {
        const correct = sprite.letter.toUpperCase() === letter.toUpperCase();
        setTimeout(() => {
          sprite.flipReveal(correct);
          if (correct) {
            this.particleLayer.spawnSparkle(
              x * CELL_SIZE + CELL_SIZE / 2,
              y * CELL_SIZE + CELL_SIZE / 2
            );
          } else {
            this.particleLayer.spawnSmoke(
              x * CELL_SIZE + CELL_SIZE / 2,
              y * CELL_SIZE + CELL_SIZE / 2,
              0xef4444
            );
          }
        }, i * staggerMs);
      }
    }
  }

  spawnVictoryConfetti() {
    const cx = (this.boardSize * CELL_SIZE) / 2;
    const cy = ((this.squares.length || this.boardSize) * CELL_SIZE) / 2;
    this.particleLayer.spawnConfetti(cx, cy, 80);
    setTimeout(() => this.particleLayer.spawnConfetti(cx - 100, cy - 50, 40), 300);
    setTimeout(() => this.particleLayer.spawnConfetti(cx + 100, cy - 50, 40), 600);
    setTimeout(() => this.particleLayer.spawnConfetti(cx, cy - 100, 50), 900);
  }

  floatScore(x: number, y: number, amount: number, color = 0xf9ca24) {
    const text = new Text({
      text: `+${amount}`,
      style: {
        fontSize: 20,
        fill: color,
        fontFamily: "monospace",
        fontWeight: "bold",
        dropShadow: { color: 0x000000, blur: 2, distance: 1, angle: Math.PI / 4 },
      },
    });
    text.anchor.set(0.5);
    text.x = x * CELL_SIZE + CELL_SIZE / 2;
    text.y = y * CELL_SIZE + CELL_SIZE / 2;
    this.floatLayer.addChild(text);

    const duration = 1200;
    const start = performance.now();
    const animate = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      text.y -= 1.5;
      text.alpha = 1 - t;
      text.scale.set(1 + t * 0.5);
      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        this.floatLayer.removeChild(text);
        text.destroy();
      }
    };
    requestAnimationFrame(animate);
  }

  pulseTile(x: number, y: number) {
    const sprite = this.tileSprites.get(`${x},${y}`);
    if (sprite) sprite.pulse();
  }

  clearParticles() {
    this.particleLayer.clear();
    for (const child of [...this.floatLayer.children]) {
      this.floatLayer.removeChild(child);
      child.destroy();
    }
  }

  highlightCell(x: number, y: number, active: boolean) {
    const sq = this.squares[y]?.[x];
    if (sq) sq.highlight(active);
  }

  highlightClue(clue: GridHint) {
    this.clearClueHighlight();
    for (let i = 0; i < clue.length; i++) {
      const x = clue.direction === "across" ? clue.startX + i : clue.startX;
      const y = clue.direction === "down" ? clue.startY + i : clue.startY;
      const sq = this.squares[y]?.[x];
      if (sq) {
        sq.highlight(true);
        this.clueHighlightKeys.add(`${x},${y}`);
      }
    }
  }

  clearClueHighlight() {
    for (const key of this.clueHighlightKeys) {
      const [x, y] = key.split(",").map(Number);
      const sq = this.squares[y]?.[x];
      if (sq) sq.highlight(false);
    }
    this.clueHighlightKeys.clear();
  }

  getCellSize() {
    return CELL_SIZE * this.currentScale;
  }

  showDebugSolution(solution: Record<string, string>) {
    this.debugSolution = solution;
    this.debugLayer.removeChildren();
    for (const [key, letter] of Object.entries(solution)) {
      const [x, y] = key.split(",").map(Number);
      const text = new Text({
        text: letter,
        style: {
          fontSize: 10,
          fill: 0x00ff00,
          fontFamily: "monospace",
          fontWeight: "bold",
        },
      });
      text.anchor.set(0.5);
      text.x = x * CELL_SIZE + CELL_SIZE / 2;
      text.y = y * CELL_SIZE + CELL_SIZE / 2;
      this.debugLayer.addChild(text);
    }
  }
}
